import { useSearchParams } from 'react-router-dom';
import { audit as auditApi, type AuditEntry } from '../api/resources.js';
import { Card, Empty, ErrorState, Loading } from '../components/ui.js';
import { formatDateTime, formatRelative } from '../lib/format.js';
import { useAsync } from '../lib/hooks.js';

/**
 * Trilha de auditoria (PRD §66).
 *
 * A trilha era escrita desde o primeiro dia e não podia ser lida sem abrir o banco. Uma
 * trilha assim é uma afirmação sobre o passado, não um controle: ela não responde "quem
 * mexeu nisso?" na hora em que a pergunta aparece.
 *
 * O que tornou isso urgente foi a impersonation de §65. Cada página que um operador da
 * Talkium abre dentro da conta do cliente vira uma linha aqui — e guardar esse registro
 * sem mostrá-lo seria o pior dos dois mundos.
 *
 * Por isso o filtro "Equipe Talkium" é um dos três, e não uma opção escondida num menu:
 * é a pergunta que o cliente tem mais motivo para fazer, e a que ele tem menos meios de
 * responder por conta própria.
 */

/**
 * O vocabulário da auditoria em português.
 *
 * As ações são chaves estáveis (`impersonation.request`), e chaves estáveis são para
 * integração, não para leitura. Uma ação que este mapa não conhece aparece crua, em vez
 * de sumir: uma linha ilegível ainda prova que algo aconteceu; uma linha ausente não.
 */
const ACOES: Record<string, string> = {
  'impersonation.start': 'Equipe Talkium iniciou acesso à conta',
  'impersonation.request': 'Equipe Talkium consultou uma página',
  'impersonation.end': 'Equipe Talkium encerrou o acesso',
  'organization.suspend': 'Conta suspensa',
  'organization.restore': 'Conta reativada',
  'organization.status_changed': 'Status da conta alterado',
  'api_key.created': 'API Key criada',
  'api_key.revoked': 'API Key revogada',
  'api_key.force_revoke': 'API Key revogada pela Talkium',
  'instance.credential_revealed': 'Equipe Talkium acessou a credencial da instância',
  'instance.created': 'Instância criada',
  'instance.deleted': 'Instância excluída',
  'instance.force_delete': 'Instância excluída pela Talkium',
  'instance.connect': 'Instância reconectada',
  'instance.pairing_code': 'Código de pareamento gerado',
  'instance.restart': 'Instância reiniciada',
  'instance.disconnect': 'Instância desconectada',
  'billing.credit_granted': 'Crédito concedido',
  'billing.plan_forced': 'Plano alterado pela Talkium',
  'billing.checkout_started': 'Contratação iniciada',
  'billing.payment_received': 'Pagamento confirmado',
  'subscription.plan_changed': 'Plano alterado',
  'member.invited': 'Membro convidado',
  'member.role_changed': 'Papel de membro alterado',
  'user.login': 'Login',
  'user.signup': 'Conta criada',
  'admin.mfa_enabled': 'Segundo fator cadastrado',
  'webhook.endpoint_created': 'Webhook cadastrado',
  'webhook.endpoint_deleted': 'Webhook removido',
  'privacy.deletion_requested': 'Exclusão de dados solicitada',
  'privacy.deletion_cancelled': 'Exclusão de dados cancelada',
};

const ATORES: Record<string, string> = {
  customer: 'Sua equipe',
  admin: 'Equipe Talkium',
  system: 'Automático',
};

export function AuditPage() {
  const [params, setParams] = useSearchParams();
  const actorType = params.get('actorType') ?? '';

  const trilha = useAsync(() => auditApi.list({ actorType: actorType || undefined }), [actorType]);

  const filtrar = (valor: string) => {
    const next = new URLSearchParams(params);
    if (valor) next.set('actorType', valor);
    else next.delete('actorType');
    setParams(next);
  };

  return (
    <Card
      title="Auditoria"
      description="Quem fez o quê nesta conta, incluindo acessos da equipe Talkium."
      bodyless
      actions={
        <select aria-label="Quem" value={actorType} onChange={(e) => filtrar(e.target.value)}>
          <option value="">Todos</option>
          <option value="customer">Sua equipe</option>
          <option value="admin">Equipe Talkium</option>
          <option value="system">Automático</option>
        </select>
      }
    >
      {trilha.loading ? (
        <Loading />
      ) : trilha.error ? (
        <ErrorState error={trilha.error} onRetry={trilha.reload} />
      ) : trilha.data?.data.length === 0 ? (
        <Empty title="Nenhum registro">
          <p className="muted">Nada foi feito nesta conta com esse filtro.</p>
        </Empty>
      ) : (
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>Quando</th>
                <th>Quem</th>
                <th>O quê</th>
                <th>Motivo</th>
              </tr>
            </thead>
            <tbody>
              {trilha.data?.data.map((entrada) => (
                <Linha key={entrada.id} entrada={entrada} />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
}

function Linha({ entrada }: { entrada: AuditEntry }) {
  const daTalkium = entrada.actor.type === 'admin';
  const caminho = typeof entrada.details?.path === 'string' ? entrada.details.path : null;

  return (
    <tr>
      <td title={formatDateTime(entrada.occurredAt)} className="subtle">
        {formatRelative(entrada.occurredAt)}
      </td>
      <td>
        <span className={`pill ${daTalkium ? 'pill-warn' : 'pill-neutral'}`}>
          {ATORES[entrada.actor.type] ?? entrada.actor.type}
        </span>
        <div className="subtle">{entrada.actor.label}</div>
      </td>
      <td>
        {ACOES[entrada.action] ?? <span className="mono">{entrada.action}</span>}
        {/* O caminho consultado, quando há um: é o que dá sentido a uma linha de
            impersonation, que sem ele diria apenas "alguém olhou alguma coisa". */}
        {caminho && <div className="subtle mono">{caminho}</div>}
        {entrada.resourceId && !caminho && <div className="subtle mono">{entrada.resourceId}</div>}
      </td>
      {/* Um motivo em branco não vira um traço discreto quando a ação é da Talkium:
          ação da equipe sem motivo declarado é justamente o que §132 não permite, e
          escondê-la atrás de um "—" faria a tela encobrir a falha. */}
      <td className={entrada.reason ? '' : 'subtle'}>
        {entrada.reason ?? (daTalkium ? 'sem motivo declarado' : '—')}
      </td>
    </tr>
  );
}
